import {
  Box,
  Button,
  Checkbox,
  Flex,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Menu,
  MenuButton,
  MenuList,
  Skeleton,
  Table,
  Tag,
  Tbody,
  Td,
  Text,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { t } from "i18next";
import { ChangeEvent, ReactNode, useState } from "react";
import { FaColumns, FaSearch, FaUndo } from "react-icons/fa";
import { SortDirection } from "../../autogen/bff-api";
import { HorizontalScrollBox } from "../HorizontalScrollBox";
import { ListLoadError } from "../ListLoadError";
import { SelectorValue } from "../input/Selector/SelectorValue";
import { Paging } from "../Paging";
import { Tooltip } from "../components/Tooltip";
import { ColumnHeader } from "./ColumnHeader";
import { filteredColumnKeys, withLockedColumnsShown } from "./columnsWithActiveFilter";
import type { NumberRangeConfig } from "./NumberRangeFilter";

export interface EntityTableColumn<T = Record<string, unknown>> {
  key: string;
  label: string;
  width?: string;
  hidden?: boolean;
  alwaysShown?: boolean;
  isCustomField?: boolean;
  isFilterActive?: boolean;
  onSearch?: (value: string) => void;
  currentSearchValue?: string;
  isTextColumn?: boolean;
  onSort?: (sort: SortDirection) => void;
  currentSortDirection?: SortDirection;
  multiSelectConfig?: {
    options: SelectorValue<T>[];
    onSelect: (values: SelectorValue<T>[]) => void | Promise<void>;
    onInputChange?: (input: string) => void | Promise<void>;
    onLoadMore?: () => void;
    selectedValues?: SelectorValue<T>[];
    optionsCount?: number;
    optionsLimit?: number;
    isLoading?: boolean;
    isError?: boolean;
    onRetry?: () => void;
    selectedValuesLoading?: boolean;
  };
  singleSelectConfig?: {
    options: SelectorValue<T>[];
    onSelect: (value: SelectorValue<T>) => void | Promise<void>;
    onInputChange?: (input: string) => void | Promise<void>;
    onLoadMore?: () => void;
    selectedValue?: SelectorValue<T>;
    optionsCount?: number;
    optionsLimit?: number;
    isLoading?: boolean;
    isError?: boolean;
    onRetry?: () => void;
  };
  dateRangeConfig?: {
    onFromDateChange: (date?: string) => void | Promise<void>;
    onToDateChange: (date?: string) => void | Promise<void>;
    fromDate?: string | null;
    toDate?: string | null;
    fromDateLabel?: string;
    toDateLabel?: string;
    presets?: { value: string; label: string }[];
    preset?: string | null;
    onPresetChange?: (preset?: string) => void | Promise<void>;
  };
  numberRangeConfig?: NumberRangeConfig;
}

export const EntityTable = ({
  columns,
  rows,
  size = "md",
  debounceMs = 300,
  isLoading,
  count,
  limit,
  skip,
  setSkip,
  onReset,
  hasActiveFilters,
  resetKey,
  onDropdownOpen,
  onDropdownClose,
  onShowColumn,
  onHideColumn,
  onShowAllColumns,
  onResetColumns,
  hasColumnChanges,
  defaultFilterColumnKeys,
  emptyText,
  noMatchText,
  noMatchContent,
  loadError,
}: {
  columns: EntityTableColumn[];
  rows: ReactNode[][];
  size?: "sm" | "md" | "lg";
  debounceMs?: number;
  isLoading: boolean;
  count?: number;
  limit: number;
  skip: number;
  setSkip: (skip: number) => void;
  onReset?: () => void;
  hasActiveFilters?: boolean;
  resetKey?: string | number;
  onDropdownOpen?: (dropdownKey: string) => void;
  onDropdownClose?: (dropdownKey: string) => void;
  onShowColumn?: (columnKey: string) => void;
  onHideColumn?: (columnKey: string) => void;
  onShowAllColumns?: (columnKeys: string[]) => void;
  onResetColumns?: () => void;
  hasColumnChanges?: boolean;
  defaultFilterColumnKeys?: string[];
  emptyText: string;
  noMatchText: string;
  noMatchContent?: ReactNode;
  loadError?: { message: string; onRetry: () => void };
}) => {
  const [columnSearchTerm, setColumnSearchTerm] = useState("");

  const filteredKeys = filteredColumnKeys(columns, defaultFilterColumnKeys);
  const shownKeys = new Set(
    withLockedColumnsShown(columns, defaultFilterColumnKeys)
      .filter((column) => !column.hidden)
      .map((column) => column.key),
  );
  const isShown = (column: EntityTableColumn) => shownKeys.has(column.key);

  const toggleColumnVisibility = (columnKey: string) => {
    if (shownKeys.has(columnKey)) onHideColumn?.(columnKey);
    else onShowColumn?.(columnKey);
  };

  const handleReset = () => {
    onReset?.();
  };

  const visibleColumnIndices = columns.flatMap((column, index) => (isShown(column) ? [index] : []));
  const visibleColumns = visibleColumnIndices.map((index) => columns[index]);

  const term = columnSearchTerm.toLowerCase();
  const menuColumns = columns
    .filter((c) => !term || c.label.toLowerCase().includes(term) || c.key.toLowerCase().includes(term))
    .sort((a, b) => a.label.localeCompare(b.label, "nb"));

  const handleColumnSearchTermChange = (e: ChangeEvent<HTMLInputElement>) => {
    setColumnSearchTerm(e.target.value);
  };

  const handleCheckboxChange = (e: ChangeEvent<HTMLInputElement>) => {
    const key = e.currentTarget.value;
    if (key) toggleColumnVisibility(key);
  };

  return (
    // Padding, not just the overflow guard: a focus ring is drawn outside the border box, and
    // setting one overflow axis makes CSS compute the other to `auto`, so this box clips the
    // toolbar buttons' rings on all four sides.
    <Flex direction="column" width="full" overflowX="hidden" p="1">
      <Flex justifyContent="space-between" pb="2">
        <Menu>
          <MenuButton
            as={Button}
            leftIcon={<Icon as={FaColumns} />}
            variant="outline"
            colorScheme="gray"
            color="smMuted"
            size="sm"
          >
            {t("Columns")}
          </MenuButton>
          <MenuList maxHeight="calc(100vh - 240px)" overflowY="auto">
            <Box px={3} py={2}>
              <Text fontSize="sm" fontWeight="medium" mb={2} color="smMuted">
                {t("Show/Hide Columns")}
              </Text>
              <InputGroup size="sm" mb={2}>
                <InputLeftElement pointerEvents="none">
                  <Icon as={FaSearch} color="smMuted" />
                </InputLeftElement>
                <Input
                  value={columnSearchTerm}
                  onChange={handleColumnSearchTermChange}
                  placeholder={`${t("Search columns")}`}
                />
              </InputGroup>
              <Text fontSize="xs" color="smMuted" mb={2}>
                {t("{{shown}} of {{total}} shown", { shown: visibleColumns.length, total: columns.length })}
              </Text>
              {(onShowAllColumns || onResetColumns) && (
                <Flex gap={2} mb={2} wrap="wrap">
                  {onShowAllColumns && (
                    <Button
                      size="xs"
                      variant="outline"
                      colorScheme="gray"
                      onClick={() => onShowAllColumns(columns.map((column) => column.key))}
                      isDisabled={visibleColumns.length === columns.length}
                    >
                      {t("Show all")}
                    </Button>
                  )}
                  {onResetColumns && (
                    <Button
                      size="xs"
                      variant="outline"
                      colorScheme="gray"
                      leftIcon={<Icon as={FaUndo} />}
                      onClick={onResetColumns}
                      isDisabled={!hasColumnChanges}
                    >
                      {t("Reset columns")}
                    </Button>
                  )}
                </Flex>
              )}
              {term && menuColumns.length === 0 && (
                <Text fontSize="sm" color="smMuted" px={2} py={1}>
                  {t("No columns match «{{term}}»", { term: columnSearchTerm })}
                </Text>
              )}
              {menuColumns.map((column) => {
                const isFiltered = filteredKeys.has(column.key);
                return (
                  <Flex key={column.key} px={2} py={1} gap={2} alignItems="center" justifyContent="space-between">
                    <Checkbox
                      value={column.key}
                      isChecked={isShown(column)}
                      isDisabled={!!column.alwaysShown || isFiltered}
                      onChange={handleCheckboxChange}
                      size="sm"
                    >
                      <Text fontSize="sm">{column.label}</Text>
                    </Checkbox>
                    <Flex gap={1}>
                      {column.isCustomField && <Tag size="sm">{t("Custom field")}</Tag>}
                      {column.alwaysShown ? (
                        <Tag size="sm">{t("Always shown")}</Tag>
                      ) : (
                        isFiltered && (
                          <Tooltip label={t("Remove the filter to hide the column")}>
                            <Tag size="sm" colorScheme="teal" tabIndex={0}>
                              {t("Filtered")}
                            </Tag>
                          </Tooltip>
                        )
                      )}
                    </Flex>
                  </Flex>
                );
              })}
            </Box>
          </MenuList>
        </Menu>
        {onReset && (
          <Button
            leftIcon={<Icon as={FaUndo} />}
            variant="outline"
            colorScheme="gray"
            color="smMuted"
            size="sm"
            onClick={handleReset}
            isDisabled={!hasActiveFilters && !hasColumnChanges}
          >
            {t("Reset table")}
          </Button>
        )}
      </Flex>
      {count !== undefined && (
        <Paging isLoading={isLoading} count={count} limit={limit} skip={skip} setSkip={setSkip} />
      )}
      <HorizontalScrollBox border="1px solid" borderColor="smBorder" rounded="md">
        <Table size={size}>
          <Thead>
            <Tr>
              {visibleColumns.map((column) => (
                <ColumnHeader
                  key={`${column.key}-${resetKey || "default"}`}
                  column={column}
                  debounceMs={debounceMs}
                  onDropdownOpen={onDropdownOpen}
                  onDropdownClose={onDropdownClose}
                />
              ))}
            </Tr>
          </Thead>
          <Tbody>
            {isLoading && rows.length === 0
              ? Array.from({ length: Math.min(limit, 5) }, (_, rowIndex) => (
                  <Tr key={rowIndex}>
                    {visibleColumnIndices.map((columnIndex, cellIndex) => (
                      <Td key={cellIndex} width={columns[columnIndex].width}>
                        <Skeleton h="5" w="full" rounded="md" />
                      </Td>
                    ))}
                  </Tr>
                ))
              : rows.map((row, rowIndex) => (
                  <Tr key={rowIndex}>
                    {visibleColumnIndices.map((columnIndex, cellIndex) => (
                      <Td
                        key={cellIndex}
                        width={columns[columnIndex].width}
                        minWidth={columns[columnIndex].width}
                        maxWidth={columns[columnIndex].width}
                      >
                        {row[columnIndex]}
                      </Td>
                    ))}
                  </Tr>
                ))}
          </Tbody>
        </Table>
      </HorizontalScrollBox>
      {!isLoading && rows.length === 0 && loadError && (
        <ListLoadError
          message={loadError.message}
          onRetry={loadError.onRetry}
          reset={hasActiveFilters && onReset ? { label: t("Reset table"), onReset: handleReset } : undefined}
        />
      )}
      {!isLoading && rows.length === 0 && !loadError && noMatchContent}
      {!isLoading && rows.length === 0 && !loadError && !noMatchContent && (
        <Flex direction="column" align="center" gap="3" py="10" textAlign="center">
          <Text color="smMuted">{hasActiveFilters ? noMatchText : emptyText}</Text>
          {hasActiveFilters && onReset && (
            <Button
              leftIcon={<Icon as={FaUndo} />}
              variant="outline"
              colorScheme="gray"
              color="smMuted"
              size="sm"
              onClick={handleReset}
            >
              {t("Reset table")}
            </Button>
          )}
        </Flex>
      )}
      {count !== undefined && count > 0 ? (
        <Paging isLoading={isLoading} count={count} limit={limit} skip={skip} setSkip={setSkip} />
      ) : null}
    </Flex>
  );
};
